/**
 * AI Studio: the owner's start page. Mo answers questions about the agent and the
 * account and builds the bot's customer agent in conversation (Build); Test checks
 * every answer against what really happened.
 * "Classic overview" makes the former overview the start page again (homeView.js).
 * Backend: /api/agent-studio/* (ultimo-bots-backend src/controller/agent_studio).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { invalidate, load, peek, put } from './studioCache';
import { agentChanged, overviewKey, richBotsKey, specKey } from './studioData';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlask, faTableCellsLarge, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { CLASSIC_PATH, MODE_EVENT, saveHomeView } from './homeView';
import { studioApi } from './studioApi';
import useAgentBuilder from './useAgentBuilder';
import BuildView from './BuildView';
import TestView from './TestView';
import StudioTour from './StudioTour';
import './agentStudio.css';

// Entering the studio it starts in Build (Robert, 18.10.2026); only the header's "Test agent" asks for Test – a
// one-time wish, removed once the studio has read it
const modeKey = (botId) => `agentStudio.mode.${botId}`;
export function readMode(botId) {
  try { return localStorage.getItem(modeKey(botId)) === 'test' ? 'test' : 'build'; } catch (_) { return 'build'; }
}
export function forgetMode(botId) {
  try { localStorage.removeItem(modeKey(botId)); } catch (_) { /* storage unavailable */ }
}

// "Classic overview" in the head row: hidden for now (Robert, 18.10.2026); the saved home view and the way back to
// the classic page stay (homeView.js), true shows the button again
const SHOW_CLASSIC_LINK = false;

/** wide: the side columns reach up to the head row (their heads on its line), so the row keeps the switch out of
 *  them – it learns the room they take on either side (the visible view's chat column), the grid centres the switch
 *  on the page while it fits and moves it towards the chat otherwise. Too little room for pill and switch: both get
 *  smaller (is-compact); not even then: the columns stay below the row, as before (is-tight). Written to the DOM
 *  directly, in the frame after a change, so a column sliding open or closed re-renders nothing. */
function useHeadRow(root, bar, mode) {
  useLayoutEffect(() => {
    const r = root.current;
    const b = bar.current;
    if (!r || !b) return undefined;
    let full = 0; // pill and switch at their full size, side by side
    const parts = () => {
      const pill = b.querySelector('.as-tour-launch');
      const sw = b.querySelector('.as-modeswitch');
      return (pill ? pill.offsetWidth + 8 : 0) + (sw ? sw.offsetWidth : 0);
    };
    const measure = () => {
      const c = r.querySelector('.as-view:not(.is-hidden) .as-build-chat, .as-view:not(.is-hidden) .as-test-chat')
        ?.getBoundingClientRect();
      const box = r.getBoundingClientRect();
      const room = c && c.width > 0 ? [c.left - box.left, box.right - c.right].map((x) => Math.max(0, Math.round(x))) : [0, 0];
      b.style.setProperty('--as-room-l', `${room[0]}px`);
      b.style.setProperty('--as-room-r', `${room[1]}px`);
      const free = box.width - room[0] - room[1] - 32; // 16px to either column
      if (!b.classList.contains('is-compact')) full = parts();
      b.classList.toggle('is-compact', free < full);
      const tight = free < full && free < parts();
      r.classList.toggle('is-tight', tight);
      if (tight) b.classList.remove('is-compact'); // the row has the whole width again
    };
    measure();
    let frame = 0;
    const later = () => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; measure(); }); };
    const late = setTimeout(later, 300); // the views mount their columns a moment later
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(later);
      ro.observe(r);
      r.querySelectorAll('.as-build-chat, .as-test-chat, .as-tour-launch, .as-modeswitch').forEach((el) => ro.observe(el));
    }
    return () => { clearTimeout(late); cancelAnimationFrame(frame); ro?.disconnect(); };
  }, [root, bar, mode]);
}

export default function AgentStudio({ botId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState(() => readMode(botId));
  const [spec, setSpec] = useState(() => peek(specKey(botId)) ?? null); // the last one at once
  // entering the studio: score and figures may have changed on another page (documents, business context),
  // outdated before the welcome mounts, so it shows the last ones and reloads them
  useState(() => invalidate(overviewKey(botId), richBotsKey()));
  const [loadError, setLoadError] = useState('');
  const [knowledgeVersion, setKnowledgeVersion] = useState(0);
  const [dataVersion, setDataVersion] = useState(0);

  useEffect(() => { forgetMode(botId); }, [botId]); // the next visit starts in Build again
  // the header's "Test agent" button switches an open studio to Test
  useEffect(() => {
    const follow = (e) => {
      setMode(e.detail === 'test' ? 'test' : 'build');
      forgetMode(botId);
    };
    window.addEventListener(MODE_EVENT, follow);
    return () => window.removeEventListener(MODE_EVENT, follow);
  }, [botId]);

  const loadSpec = useCallback(() => {
    setLoadError('');
    load(specKey(botId), () => studioApi.get(botId, '')).then(setSpec).catch((e) => setLoadError(e.message || String(e)));
  }, [botId]);
  useEffect(() => { loadSpec(); }, [loadSpec]);
  // every change (the panel, Mo) is what the next visit starts with
  useEffect(() => { if (spec && String(spec.id) === String(botId)) put(specKey(botId), spec); }, [spec, botId]);
  // a change to the agent or its knowledge can move the success score and the figures: outdated at once,
  // reloaded shortly after the last of a burst of changes (Mo often changes several things in a row)
  const seen = useRef(null);
  const version = spec && String(spec.id) === String(botId) ? spec.version : null;
  useEffect(() => {
    const sig = `${version}|${knowledgeVersion}`;
    const before = seen.current;
    seen.current = sig;
    if (version == null || before == null || before === sig) return undefined;
    invalidate(overviewKey(botId), richBotsKey());
    const id = setTimeout(() => agentChanged(botId), 1200);
    return () => clearTimeout(id);
  }, [version, knowledgeVersion, botId]);

  const builder = useAgentBuilder(botId, {
    onSpec: (s) => setSpec((cur) => (cur && cur.id !== s.id ? cur : s)),
    onKnowledge: () => setKnowledgeVersion((v) => v + 1),
    onData: () => setDataVersion((v) => v + 1),
  });
  const busy = builder.state?.running;
  const waiting = builder.pending.length > 0;
  const rootRef = useRef(null);
  const barRef = useRef(null);
  useHeadRow(rootRef, barRef, mode);

  return (
    <div className="as-root" ref={rootRef}>
      <div className="as-modebar" ref={barRef}>
        <div className="as-modebar-side is-start"><StudioTour /></div>
        <div className="as-modeswitch" role="tablist">
          <button type="button" role="tab" aria-selected={mode === 'build'} className={mode === 'build' ? 'is-active' : ''}
            onClick={() => setMode('build')}>
            <FontAwesomeIcon icon={faWandMagicSparkles} /> {t('agent_studio_tab_build')}
            {(busy || waiting) && <span className={`as-dot ${waiting ? 'is-waiting' : 'is-busy'}`} />}
          </button>
          <button type="button" role="tab" aria-selected={mode === 'test'} className={mode === 'test' ? 'is-active' : ''}
            onClick={() => setMode('test')}>
            <FontAwesomeIcon icon={faFlask} /> {t('agent_studio_tab_test')}
          </button>
        </div>
        <div className="as-modebar-side is-end">
          {SHOW_CLASSIC_LINK && (
            <button type="button" className="as-classic-link" title={t('agent_studio_classic_view_hint')}
              onClick={() => { saveHomeView('classic'); navigate(CLASSIC_PATH); }}>
              <FontAwesomeIcon icon={faTableCellsLarge} /> <span>{t('agent_studio_classic_view')}</span>
            </button>
          )}
        </div>
      </div>
      {loadError && (
        <div className="as-pad">
          <div className="as-error">{t('agent_studio_load_error')} ({loadError}) <button type="button" className="as-btn-link" onClick={loadSpec}>{t('agent_studio_retry')}</button></div>
        </div>
      )}
      <div className="as-stage">
        <div className={mode === 'build' ? 'as-view' : 'as-view is-hidden'}>
          <BuildView botId={botId} spec={spec} setSpec={setSpec} builder={builder}
            knowledgeVersion={knowledgeVersion} goTest={() => setMode('test')} />
        </div>
        <div className={mode === 'test' ? 'as-view' : 'as-view is-hidden'}>
          <TestView botId={botId} spec={spec} dataVersion={dataVersion} />
        </div>
      </div>
    </div>
  );
}
